import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, BookOpen, ExternalLink, ChevronLeft, ChevronRight, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Card } from "@/components/ui-em";

const PAGE_SIZE = 10;

export default function AdminKnowledge() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    api
      .get("/kb/articles", {
        params: {
          q: q || undefined,
          category: category !== "all" ? category : undefined,
          status: status !== "all" ? status : undefined,
          raw: 1, // Admin sees source content, not translated
        },
      })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/kb/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setPage(1);
    load();
  }, [q, category, status]);

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/kb/articles/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · Content"
        title="Knowledge Management"
        description="Create, edit and organize every article in the Knowledge Base."
        actions={
          <Button as={Link} to="/knowledge-base/new" icon={Plus} variant="primary" size="lg" data-testid="admin-new-article">
            New article
          </Button>
        }
      />

      <div className="flex flex-col md:flex-row gap-3">
        <SearchInput value={q} onChange={setQ} placeholder="Search articles..." className="flex-1" data-testid="admin-kb-search" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="em-input md:w-56" data-testid="admin-kb-category">
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="em-input md:w-40" data-testid="admin-kb-status">
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading...</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No articles yet"
            description="Start building your knowledge base by creating your first article."
            actionLabel="Create first article"
            actionTo="/knowledge-base/new"
            className="m-4"
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                    <th className="px-6 py-4 font-semibold">Title</th>
                    <th className="px-6 py-4 font-semibold">Category</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Updated</th>
                    <th className="px-6 py-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((a) => (
                    <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02] group" style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium truncate max-w-md">{a.title}</span>
                          {a.is_internal && (
                            <Badge variant="outline" size="xs" uppercase icon={EyeOff}>
                              Internal
                            </Badge>
                          )}
                        </div>
                        {a.tags?.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {a.tags.slice(0, 3).map((t) => (
                              <span key={t} className="text-[10px] text-zinc-500">#{t}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gold">{a.category}</span>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={a.status === "published" ? "success" : "warn"} uppercase>
                          {a.status || "published"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-xs text-zinc-500">
                        {new Date(a.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/knowledge-base/${a.id}`}
                            className="p-2 rounded-md text-zinc-500 hover:text-white hover:bg-white/5"
                            style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                            title="View"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Link>
                          <Link
                            to={`/knowledge-base/${a.id}/edit`}
                            data-testid={`admin-edit-${a.id}`}
                            className="p-2 rounded-md text-zinc-500 hover:text-gold hover:bg-gold/5"
                            style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Link>
                          <button
                            onClick={() => remove(a.id)}
                            data-testid={`admin-delete-${a.id}`}
                            className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                            style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-white/5 text-xs text-zinc-500">
                <div>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, items.length)} of {items.length}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <span className="px-2">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
